import { useState } from 'react'
import {
  EmptyRow, ErpTable, KpiStrip, MetricCard, Panel, Td, Th,
} from '@/components/erp/primitives'
import { EmptyState } from '@/shared/ui/states'
import { fmtNumber, humanise } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import type { QuadrantPoint, QuadrantResponse } from '@/shared/api/types'
import { analyticsCostVsUplift } from '../api'
import { MeasuredWith, MixedCurrencyNote, Money, NotRun, Unavailable } from '../parts'
import { CategoryBadge } from '../vocab'
import { AnalyticsScreen } from './shell'

/**
 * What a point of support cost, per programme.
 *
 * ## Why the quadrant labels are not on the chart
 *
 * PollSphere printed "Workhorse / Star / Question mark / Dog" into the four
 * corners. A programme lands in a corner because of where two medians happen to
 * fall in the current filter, and those words then get repeated in meetings as
 * though they were a finding about the programme. The medians are drawn — they
 * are useful — and the naming is left to the reader, who has to look at the
 * measurement coverage before the position means anything.
 *
 * ## Nothing here is coerced to zero
 *
 * A programme with no measured uplift is not plotted at y=0. Doing that puts
 * every cheap unmeasured programme in the bottom-left and calls it bad value.
 * They are listed below the chart with the reason they could not be rated, and
 * `excludedNullUplift` says how many.
 *
 * ## The error bar is the point
 *
 * `upliftStdErr` on a programme with 14 measurements is enormous, and a dot
 * without it looks exactly like a dot with 4,000 behind it. The bar is drawn
 * whenever the server sends one, and the measurement coverage is in the
 * tooltip and in the table.
 */
export function ProgramCostVsUpliftScreen() {
  return (
    <AnalyticsScreen
      title="Cost vs uplift"
      description="Attributed cost per beneficiary against measured change in support."
      load={(f) => analyticsCostVsUplift(f)}
      isEmpty={(d) => (d.points?.length ?? 0) === 0 && (d.unrated?.length ?? 0) === 0}
    >
      {(d) => <QuadrantBody d={d} />}
    </AnalyticsScreen>
  )
}

const PLOT_H = 300

function QuadrantBody({ d }: { d: QuadrantResponse }) {
  const [hover, setHover] = useState<string | null>(null)
  // A server point missing either coordinate cannot be placed. It appears in
  // neither `unrated` nor the plot, so without this count it disappears from
  // the screen entirely.
  const allPoints = d.points ?? []
  const points = allPoints.filter((p) => p.costPerBeneficiary != null && p.uplift != null)
  const unplottable = allPoints.length - points.length

  const log = d.costAxisScale === 'LOG'
  const xMinRaw = d.costAxisMin == null ? 0 : Number(d.costAxisMin)
  const xMaxRaw = d.costAxisMax == null ? 1 : Number(d.costAxisMax)
  const yMin = d.upliftAxisMin == null ? 0 : Number(d.upliftAxisMin)
  const yMax = d.upliftAxisMax == null ? 1 : Number(d.upliftAxisMax)

  // A log axis needs a positive floor. The server already excludes zero-cost
  // programmes when it chooses LOG, so this only guards a degenerate response.
  const tx = (v: number) => (log ? Math.log10(Math.max(v, 0.01)) : v)
  const xMin = tx(xMinRaw)
  const xMax = tx(xMaxRaw)
  const xPct = (v: number) => clamp(((tx(v) - xMin) / Math.max(xMax - xMin, 1e-9)) * 100)
  const yPct = (v: number) => clamp(((v - yMin) / Math.max(yMax - yMin, 1e-9)) * 100)

  const medCost = d.medianCost == null ? null : Number(d.medianCost)
  const medUplift = d.medianUplift == null ? null : Number(d.medianUplift)
  const maxBen = Math.max(...points.map((p) => p.beneficiaries), 1)
  const hovered = points.find((p) => p.programId === hover)

  return (
    <div className="space-y-4">
      <KpiStrip>
        <MetricCard label="Programmes plotted" value={fmtNumber(points.length)}
          sub={`${fmtNumber(d.unrated?.length ?? 0)} could not be rated${
            unplottable > 0 ? ` · ${fmtNumber(unplottable)} missing a coordinate` : ''}`} />
        <MetricCard
          label="Median cost per beneficiary"
          value={medCost == null
            ? <Unavailable reason={d.medianUnavailableReason} short />
            : <Money value={medCost} currency={d.currency} mixedReason={d.currencyMixedReason} />}
          sub={log ? 'Cost axis is logarithmic' : 'Cost axis is linear'}
        />
        <MetricCard
          label="Median uplift"
          value={medUplift == null
            ? <Unavailable reason={d.medianUnavailableReason} short />
            : <span className="tabular-nums">{medUplift > 0 ? '+' : ''}{medUplift.toFixed(1)} pp</span>}
          sub="Across the plotted programmes only"
        />
        <MetricCard
          label="Excluded"
          value={fmtNumber(d.excludedNullUplift)}
          sub={(
            <span title="Never plotted at zero. A programme nobody measured has not been shown to do nothing.">
              programmes with no measured uplift
            </span>
          )}
        />
      </KpiStrip>

      <MixedCurrencyNote reason={d.currencyMixedReason}
        what="A position on the horizontal axis" />

      <Panel
        title="What a point of support cost"
        description="Each dot is one programme: further right means it cost more per person,
                     higher up means support rose more where it ran. A big dot reached more
                     people. The line through a dot shows how shaky its figure is — a tall
                     line means it rests on very few measurements, so do not read much into
                     where that dot sits."
      >
        {d.currencyMixedReason ? (
          // The bars on the money screen refuse to be drawn across currencies
          // because a bar compares by LENGTH. A scatter compares by POSITION,
          // which is the same claim through a different channel: two dots side
          // by side would say two programmes cost the same when one is in
          // dollars. The table below keeps every figure, where each stands alone.
          <NotRun what="This plot" reason={d.currencyMixedReason} />
        ) : points.length === 0 ? (
          // An empty plot area is worse than no plot area: 300px of blank space
          // reads as a chart that failed to draw. Say why there is nothing on it.
          <EmptyState
            title="Nothing to plot"
            hint={`No programme in scope has both an attributed cost and a measured uplift. ${
              d.unrated?.length ? `${d.unrated.length} are below the sample gate or have no cost recorded` : ''
            }${d.unrated?.length && d.excludedNullUplift ? ' and ' : ''}${
              d.excludedNullUplift ? `${d.excludedNullUplift} have no measured uplift` : ''
            }. They are listed below rather than drawn at zero.`}
          />
        ) : (
        <>
        {/* The plot is inset because a dot is a MARK WITH A RADIUS: clamping its
            centre into the box still hangs half of it over the edge, and the
            cheapest programme in the portfolio is exactly the one that ends up
            sliced in half by the panel border. */}
        <div className="relative mx-5 my-3" style={{ height: PLOT_H }}
          role="img"
          aria-label={`Scatter of ${points.length} programmes, cost per beneficiary against measured uplift.`}>
          {/* median guides — recessive, dashed, labelled clear of the plot */}
          {medCost != null && (
            <div className="absolute inset-y-0 border-l border-dashed border-border"
              style={{ left: `${xPct(medCost)}%` }}>
              <span className="absolute -bottom-3 left-1 text-erp-xs text-muted-foreground/70 whitespace-nowrap">
                median cost
              </span>
            </div>
          )}
          {medUplift != null && (
            <div className="absolute inset-x-0 border-t border-dashed border-border"
              style={{ bottom: `${yPct(medUplift)}%` }}>
              <span className="absolute -top-4 left-0 text-erp-xs text-muted-foreground/70 whitespace-nowrap">
                median uplift
              </span>
            </div>
          )}
          {/* zero line, where the range crosses it — "no change" is not the same as "median" */}
          {yMin < 0 && yMax > 0 && (
            <div className="absolute inset-x-0 border-t border-border"
              style={{ bottom: `${yPct(0)}%` }}>
              <span className="absolute -top-4 right-0 text-erp-xs text-muted-foreground/80 whitespace-nowrap">
                no change
              </span>
            </div>
          )}

          {points.map((p) => {
            const x = xPct(Number(p.costPerBeneficiary))
            const y = yPct(Number(p.uplift))
            const size = 8 + Math.sqrt(p.beneficiaries / maxBen) * 18
            const se = p.upliftStdErr == null ? null : Number(p.upliftStdErr)
            const seSpan = se == null ? 0 : (se / Math.max(yMax - yMin, 1e-9)) * PLOT_H
            // A standard error wider than the whole y range is common on a
            // programme with a dozen measurements — and drawn unclipped it runs
            // out of the panel and down over the table below it. Clip it to the
            // plot box: a bar that reaches the edge already says "wider than
            // this chart can show".
            const yPx = (y / 100) * PLOT_H
            const seDown = Math.min(seSpan, yPx)
            const seUp = Math.min(seSpan, PLOT_H - yPx)
            const on = hover === p.programId
            return (
              <div key={p.programId} className="absolute" style={{ left: `${x}%`, bottom: `${y}%` }}>
                {se != null && seSpan > 1 && (
                  // The error bar wears the DOT's hue, faintly, so it reads as
                  // part of its mark. Drawn in ink it was indistinguishable from
                  // the dashed median guides, and a reader counted four
                  // reference lines that were not there.
                  <div
                    className="absolute left-0 -translate-x-1/2 rounded-full"
                    style={{
                      width: 3,
                      background: 'color-mix(in oklab, var(--chart-1) 45%, transparent)',
                      height: seDown + seUp,
                      bottom: -seDown,
                    }}
                    aria-hidden
                  />
                )}
                <button
                  type="button"
                  className={cn(
                    'absolute -translate-x-1/2 translate-y-1/2 rounded-full ring-2 ring-card transition-opacity',
                    on ? 'opacity-100' : 'opacity-80 hover:opacity-100',
                  )}
                  style={{ width: size, height: size, background: 'var(--chart-1)' }}
                  onMouseEnter={() => setHover(p.programId)}
                  onMouseLeave={() => setHover(null)}
                  onFocus={() => setHover(p.programId)}
                  onBlur={() => setHover(null)}
                  aria-label={`${p.name}: ${fmtNumber(p.beneficiaries)} beneficiaries`}
                />
              </div>
            )
          })}

          {hovered && (
            <div className="absolute top-0 right-0 z-20 rounded-md border border-border bg-popover px-2 py-1.5 text-erp-xs shadow-md max-w-[18rem]">
              <div className="font-medium truncate">{hovered.name}</div>
              <div className="text-muted-foreground">
                <Money value={hovered.costPerBeneficiary} currency={d.currency} mixedReason={d.currencyMixedReason} /> per beneficiary
                {' · '}{Number(hovered.uplift) > 0 ? '+' : ''}{Number(hovered.uplift).toFixed(1)} pp
              </div>
              <div className="text-muted-foreground">
                <MeasuredWith measured={hovered.measured} measurable={hovered.measurable} />
                {hovered.usedDid
                  ? ' · against a control cohort'
                  : ' · before-and-after, no control'}
              </div>
            </div>
          )}
        </div>
        {/* The axis extremes carry no currency symbol when the selection spans
            more than one — a "KES 4,000" label on an axis that also holds
            dollars is the specific error the banner above is warning about. */}
        <div className="flex justify-between text-erp-xs text-muted-foreground/70 mt-1">
          <span>{d.currencyMixedReason ? fmtNumber(Math.round(xMinRaw)) : <Money value={xMinRaw} currency={d.currency} mixedReason={d.currencyMixedReason} />}</span>
          <span>cost per beneficiary{log ? ' (log scale)' : ''}{d.currencyMixedReason ? ' · mixed currencies' : ''}</span>
          <span>{d.currencyMixedReason ? fmtNumber(Math.round(xMaxRaw)) : <Money value={xMaxRaw} currency={d.currency} mixedReason={d.currencyMixedReason} />}</span>
        </div>
        </>
        )}
      </Panel>

      <Panel title="Programmes" bodyClassName="p-0">
        <ErpTable head={(
          <>
            <Th>Programme</Th>
            <Th>Category</Th>
            <Th className="text-right">Beneficiaries</Th>
            <Th className="text-right">Cost each</Th>
            <Th className="text-right">Uplift</Th>
            <Th>Measured</Th>
            <Th>Method</Th>
          </>
        )}>
          {points.length === 0
            ? <EmptyRow colSpan={7} message="No programme in scope has both a cost and a measured uplift." />
            : [...points].sort(byUpliftDesc).map((p) => (
              <tr key={p.programId}>
                <Td className="font-medium max-w-[16rem]"><span className="truncate block" title={p.name}>{p.name}</span></Td>
                <Td><CategoryBadge code={p.categoryCode} /></Td>
                <Td className="text-right tabular-nums">{fmtNumber(p.beneficiaries)}</Td>
                <Td className="text-right"><Money value={p.costPerBeneficiary} currency={d.currency} mixedReason={d.currencyMixedReason} /></Td>
                <Td className="text-right tabular-nums">
                  {Number(p.uplift) > 0 ? '+' : ''}{Number(p.uplift).toFixed(1)} pp
                  {p.upliftStdErr != null && (
                    <span className="text-muted-foreground"> ± {Number(p.upliftStdErr).toFixed(1)}</span>
                  )}
                </Td>
                <Td><MeasuredWith measured={p.measured} measurable={p.measurable} /></Td>
                <Td className="text-erp-xs text-muted-foreground">
                  {p.usedDid ? 'Difference-in-differences' : 'Before and after'}
                </Td>
              </tr>
            ))}
        </ErpTable>
      </Panel>

      {(d.unrated?.length ?? 0) > 0 && (
        <Panel title="Not rated" description="On the chart these would be indistinguishable from a programme that was measured and found to do nothing." bodyClassName="p-0">
          <ErpTable head={(
            <>
              <Th>Programme</Th>
              <Th className="text-right">Beneficiaries</Th>
              <Th>Why not</Th>
            </>
          )}>
            {d.unrated.map((u) => (
              <tr key={u.programId}>
                <Td className="font-medium">{u.name}</Td>
                <Td className="text-right tabular-nums">{fmtNumber(u.beneficiaries)}</Td>
                <Td className="text-erp-xs text-muted-foreground">{humanise(u.reasonCode)}</Td>
              </tr>
            ))}
          </ErpTable>
        </Panel>
      )}

      <p className="text-erp-xs text-muted-foreground">
        Uplift is in percentage points of support, measured before enrolment and again
        afterwards. It is a change in what people told a mobilizer, not a vote count, and
        the programmes marked before-and-after have nothing to separate the programme's
        effect from everything else that happened in the same months.
      </p>
    </div>
  )
}

function byUpliftDesc(a: QuadrantPoint, b: QuadrantPoint) {
  return Number(b.uplift ?? 0) - Number(a.uplift ?? 0)
}

/** Keeps a dot's CENTRE far enough inside that its radius stays in the box. */
function clamp(n: number) {
  return Math.max(3, Math.min(97, n))
}
